import type { IAllProps as EditorProps } from '@hugerte/hugerte-react';

import type { Theme } from '@change-corgi/design-system-legacy/theme';
import { useTheme } from '@change-corgi/design-system-legacy/theme';

import localeMapping from '#config/hugerte/locale-mapping.json';

import { useLocale } from '#src/app/shared/hooks/l10n';

/**
 * Hyperlinks are being styled separately since hyperlinks are
 * considered content within the HugeRte editor, which is handled by
 * the content_style attribute in the editor config.
 */
const getHugeRteContentStyle = (theme: Theme): string => `
  a {
    color: ${theme.colors['primary-changeRed']};
  }
`;

type EditorInitConfig = EditorProps['init'];

const BASE_URL = '/corgi/assets/vendors/hugerte/1.0.10';

const hugeRteStaticConfig: EditorInitConfig = {
	anchor_bottom: false,
	anchor_top: false,
	base_url: BASE_URL,
	branding: false,
	browser_spellcheck: true,
	convert_urls: false,
	min_height: 350,
	inline_boundaries: false,
	link_assume_external_targets: 'https',
	link_context_toolbar: true,
	link_default_target: '_blank',
	link_target_list: false,
	link_title: false,
	menubar: false,
	oninit: 'setPlainText',
	paste_as_text: true,
	paste_remove_spans: true,
	paste_remove_styles: true,
	plugins: 'link image lists help',
	relative_urls: false,
	skin: 'hugerte-5',
	statusbar: false,
	toolbar: 'bold italic | numlist bullist | link | help',
	promotion: false,
	// disabled for now as the "paste" command doesn't work due to browser security (see https://github.com/hugerte/hugerte/issues/7254)
	// the "link" command remains in the toolbar anyway
	contextmenu: false,
	// https://github.com/hugerte/hugerte/security/advisories/GHSA-5359-pvf2-pw78
	convert_unsafe_embeds: true,
	// https://github.com/hugerte/hugerte/security/advisories/GHSA-438c-3975-5x3f
	sandbox_iframes: true,
};

export const useDefaultConfig = (): EditorInitConfig => {
	const theme = useTheme();
	const locale = useLocale();
	const localeCode = localeMapping.localeMapping[locale];

	return {
		...hugeRteStaticConfig,
		content_style: getHugeRteContentStyle(theme),
		language: localeCode,
		language_url: `${BASE_URL}/langs/${localeCode}.js`,
	};
};
